/**
 * DataBest — shared Web Speech API hook.
 * Continuous dictation with a silence auto-stop. Delivers the full transcript
 * via onFinal(text) when recognition ends.
 */
import { useCallback, useEffect, useRef, useState } from "react";

const SILENCE_TIMEOUT_MS = 30000;

const getCtor = () =>
    typeof window !== "undefined"
        ? window.SpeechRecognition || window.webkitSpeechRecognition
        : null;

export default function useSpeechRecognition({ onFinal }) {
    const [supported, setSupported] = useState(true);
    const [listening, setListening] = useState(false);
    const [interim, setInterim] = useState("");

    const recRef = useRef(null);
    const finalRef = useRef("");
    const interimRef = useRef("");
    const silenceRef = useRef(null);
    const onFinalRef = useRef(onFinal);
    onFinalRef.current = onFinal;

    useEffect(() => {
        setSupported(!!getCtor());
    }, []);

    const clearTimer = () => {
        if (silenceRef.current) {
            clearTimeout(silenceRef.current);
            silenceRef.current = null;
        }
    };

    const arm = () => {
        clearTimer();
        silenceRef.current = setTimeout(() => {
            try {
                recRef.current?.stop();
            } catch (_) {
                /* ignore */
            }
        }, SILENCE_TIMEOUT_MS);
    };

    const start = useCallback(() => {
        const Ctor = getCtor();
        if (!Ctor) {
            setSupported(false);
            return;
        }
        const rec = new Ctor();
        rec.continuous = true;
        rec.interimResults = true;
        rec.lang =
            navigator.language ||
            (navigator.languages && navigator.languages[0]) ||
            "en-US";

        finalRef.current = "";
        interimRef.current = "";
        setInterim("");

        rec.onstart = () => {
            setListening(true);
            arm();
        };
        rec.onresult = (e) => {
            let itm = "";
            for (let i = e.resultIndex; i < e.results.length; i++) {
                const r = e.results[i];
                const chunk = r[0]?.transcript ?? "";
                if (r.isFinal) {
                    finalRef.current = (finalRef.current + " " + chunk).trim();
                } else {
                    itm += chunk;
                }
            }
            interimRef.current = itm;
            setInterim(itm);
            arm();
        };
        rec.onerror = () => {
            clearTimer();
            setListening(false);
        };
        rec.onend = () => {
            clearTimer();
            setListening(false);
            const text = [finalRef.current, interimRef.current]
                .filter(Boolean)
                .join(" ")
                .trim();
            onFinalRef.current?.(text);
        };

        recRef.current = rec;
        try {
            rec.start();
        } catch (_) {
            setListening(false);
        }
    }, []);

    const stop = useCallback(() => {
        clearTimer();
        try {
            recRef.current?.stop();
        } catch (_) {
            /* ignore */
        }
    }, []);

    useEffect(() => {
        return () => {
            clearTimer();
            try {
                recRef.current?.abort();
            } catch (_) {
                /* ignore */
            }
        };
    }, []);

    return { supported, listening, interim, start, stop };
}
